import { Box } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { signOutUser } from '@/domain/auth/actions/sign-out-user.action';
import {
  selectUserFullname,
  selectUserLicensesMap,
  selectUserTrial,
} from '@/domain/auth/auth.selectors';
import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { ModalSettings } from '@/ui/pages/app/settings/ModalSettings';
import { ModalSurveys } from '@/ui/pages/survey/ModalSurveys';

import { EndTrial } from './EndTrial';
import { Menu } from './Menu';
import { TrialMessageBar } from './TrialMessageBar';

export const DEFAULT_LAYOUT_MARGIN = 4;

interface IProps {
  children?: React.ReactNode;
  enableMargin?: boolean;
  enableTrialOut?: boolean;
}

export function DefaultLayout({
  children,
  enableMargin = true,
  enableTrialOut = false,
}: IProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();

  const userFullname = useAppSelector(selectUserFullname);
  const userTrial = useAppSelector(selectUserTrial);
  const userLicensesMap = useAppSelector(selectUserLicensesMap);
  const organizationInfos = useAppSelector(selectOrganisationInfos);

  const displayTrialMessageBar =
    userTrial.isOnTrial &&
    Boolean(userTrial.endTime) &&
    dayjs(userTrial.endTime).diff(dayjs(), 'd') >= 0;

  const displayEndTrialMessage =
    !userTrial.isOnTrial && userLicensesMap.size === 0 && !enableTrialOut;

  const onClickMyProjects = async () => {
    navigate('/app/projects/festival');
  };
  const onClickMyTours = async () => {
    navigate('/app/projects/tour');
  };

  const onClickMyVenues = async () => {
    navigate('/app/projects/venue');
  };

  const onClickMyResources = async () => {
    const url = import.meta.env.VITE_URL_MY_RESOURCES;
    window.open(url, '_blank');
  };

  const [modalMySurveysOpen, setModalMySurveysOpen] = React.useState(false);
  const onClickMySurveys = () => setModalMySurveysOpen(true);

  const [modalSettingsOpen, setModalSettingsOpen] = React.useState(false);
  const onClickSettings = () => setModalSettingsOpen(true);

  const onClickSubscription = async () => {
    const url = import.meta.env.VITE_URL_STRIPE_SUBSCRIPTION;
    window.open(url, '_blank');
  };

  const onClickSignOut = async () => {
    try {
      await dispatch(signOutUser()).unwrap();
      navigate('/');
    } catch (error) {
      console.error(error);
    }
  };

  const onClickGoPrenium = async () => {
    navigate('/app/contact');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <ModalSurveys
        open={modalMySurveysOpen}
        onClose={() => setModalMySurveysOpen(false)}
      />
      <ModalSettings open={modalSettingsOpen} setOpen={setModalSettingsOpen} />
      <Menu
        userName={userFullname}
        organisationLogo={organizationInfos.logo}
        currentLocationPath={location.pathname}
        disableSubscriptionButton={userTrial.isOnTrial}
        onClickMyProjects={onClickMyProjects}
        onClickMyTours={onClickMyTours}
        onClickMyVenues={onClickMyVenues}
        onClickMyResources={onClickMyResources}
        onClickMySurveys={onClickMySurveys}
        onClickSettings={onClickSettings}
        onClickSubscription={onClickSubscription}
        onClickSignOut={onClickSignOut}
      />
      <Box
        sx={{
          margin: enableMargin ? DEFAULT_LAYOUT_MARGIN : 0,
          width: '100%',
        }}
      >
        {displayEndTrialMessage ? <EndTrial /> : children}
      </Box>
      {displayTrialMessageBar && (
        <TrialMessageBar
          userTrial={userTrial}
          onClickGoPrenium={onClickGoPrenium}
        />
      )}
    </Box>
  );
}
